import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { ToastService } from '@app/services/toast.service';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ColDef } from 'ag-grid-community';
import { finalize } from 'rxjs';

type Row = {
  name: string;
  email: string;
  taxNumber?: string;
  country?: string;
};

@Component({
  selector: 'app-investor-list',
  standalone: true,
  imports: [CommonModule, GridTableComponent, TranslatePipe],
  templateUrl: './investor-list.component.html',
  styleUrl: './investor-list.component.scss',
})
export class InvestorListComponent {
  private readonly legalEntityService = inject(LegalEntityService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  investors: Row[] = [];
  columns: ColDef[] = [];
  readonly isLoading = signal(true);

  ngOnInit(): void {
    this.buildColumns();
    this.translate.onLangChange.subscribe(() => this.buildColumns());
    this.load();
  }

  private buildColumns(): void {
    this.columns = [
      {
        field: 'name',
        headerName: this.translate.instant('admin.investors.table.name'),
      },
      {
        field: 'email',
        headerName: this.translate.instant('admin.investors.table.email'),
      },
      {
        field: 'taxNumber',
        headerName: this.translate.instant('admin.investors.table.vat'),
      },
      {
        field: 'country',
        headerName: this.translate.instant('admin.investors.table.country'),
      },
    ];
  }

  private load(): void {
    this.isLoading.set(true);
    this.legalEntityService
      .getInvestors()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (res) => {
          const data = res.data ?? [];
          this.investors = data;
        },
        error: () => {
          this.investors = [];
          this.toast.error(
            this.translate.instant('admin.toasts.tableLoadFailed'),
          );
        },
      });
  }
}
